import { useGetSchemaQuery } from 'hooks/customerApi';
import { useCustomerApiContext } from 'hooks/useCustomerApiContext';
import { useFlagEnabledWithOverrides } from 'hooks/useFlagEnabledWithOverrides';
import { useMemo } from 'react';
import { makePath } from 'utils/routes';

import {
  BaseSchema,
  type BaseSchemaLedgerAccountType,
  SchemaLedgerEntityStatusSchema,
} from '@fragment/types/schemas';

import { type NavTree, setActiveNavTreePath } from './SideNav';

const hasArchivedAccount = (accounts: BaseSchemaLedgerAccountType[]): boolean =>
  accounts.some(
    (account) =>
      account.status === SchemaLedgerEntityStatusSchema.Enum.archived ||
      hasArchivedAccount(account.children ?? [])
  );

export type LedgersNavTreeProps = {
  active: string;
  workspaceId: string;
  schemaKey?: string;
  ledgerId?: string;
  accountId?: string;
  groupId?: string;
};

export const useLedgersNavTree = ({
  active,
  workspaceId,
  schemaKey,
  ledgerId: routeLedgerId,
  accountId,
  groupId,
}: LedgersNavTreeProps) => {
  const isPaymentsEnabled = useFlagEnabledWithOverrides(
    'dashboard-ledger-payments'
  );
  const { context, token } = useCustomerApiContext();
  // The sidebar is outside the routed page's SchemaMetadataProvider.
  const variables = useMemo(() => ({ key: schemaKey ?? '' }), [schemaKey]);
  const [{ data }] = useGetSchemaQuery({
    variables,
    context,
    pause: !token || !schemaKey,
  });
  const ledgerId =
    routeLedgerId ??
    (data?.schema?.key === schemaKey
      ? data?.schema?.ledgers.nodes[0]?.id
      : undefined);
  const storedSchema =
    data?.schema?.key === schemaKey
      ? data?.schema?.versions.nodes[0]?.json
      : undefined;
  // Parsing the full schema is shared across renders of the same query result.
  const schema = useMemo(
    () => (storedSchema ? BaseSchema.parse(storedSchema) : undefined),
    [storedSchema]
  );
  const hasGroups =
    !schemaKey ||
    Boolean(
      schema?.groups?.length ||
        schema?.ledgerEntries?.types.some((entry) => entry.groups?.length)
    );
  const hasMigrations = Boolean(
    schemaKey &&
      schema &&
      (hasArchivedAccount(schema.chartOfAccounts.accounts) ||
        schema.ledgerEntries?.types.some(
          (entry) =>
            entry.status === SchemaLedgerEntityStatusSchema.Enum.archived
        ))
  );
  return useMemo(() => {
    const navTree: NavTree = {
      ledgers: {
        props: {
          href: makePath(
            [
              'w',
              workspaceId,
              'projects',
              ...(schemaKey
                ? ['schema', schemaKey, 'ledgers']
                : [ledgerId ?? '/', 'home']),
            ],
            {
              absolute: true,
            }
          ),
          children: 'Ledgers',
        },
        visibleOverride: true,
        subNav: {
          accounts: {
            props: {
              href: ledgerId
                ? makePath(
                    [
                      'w',
                      workspaceId,
                      'projects',
                      ...(schemaKey ? ['schema', schemaKey] : []),
                      ledgerId,
                      'accounts',
                    ],
                    { absolute: true }
                  )
                : '',
              children: 'Accounts',
              'aria-disabled': !ledgerId,
            },
            visibleOverride: active.startsWith('projects.ledgers.accounts.'),
            subNav: {
              childAccounts: {
                props: {
                  href:
                    ledgerId && accountId
                      ? makePath(
                          [
                            'w',
                            workspaceId,
                            'projects',
                            ...(schemaKey ? ['schema', schemaKey] : []),
                            ledgerId,
                            'accounts',
                            accountId,
                            'childaccounts',
                          ],
                          { absolute: true }
                        )
                      : '',
                  children: 'Children',
                },
              },
              lines: {
                props: {
                  href:
                    ledgerId && accountId
                      ? makePath(
                          [
                            'w',
                            workspaceId,
                            'projects',
                            ...(schemaKey ? ['schema', schemaKey] : []),
                            ledgerId,
                            'accounts',
                            accountId,
                            'lines',
                          ],
                          { absolute: true }
                        )
                      : '',
                  children: 'Lines',
                },
              },
              balances: {
                props: {
                  href:
                    ledgerId && accountId
                      ? makePath(
                          [
                            'w',
                            workspaceId,
                            'projects',
                            ...(schemaKey ? ['schema', schemaKey] : []),
                            ledgerId,
                            'accounts',
                            accountId,
                            'balances',
                          ],
                          { absolute: true }
                        )
                      : '',
                  children: 'Balances',
                },
                // Only show subtree if we're already within it
                visibleOverride: active.startsWith(
                  'projects.ledgers.accounts.balances.'
                ),
                subNav: {
                  total: {
                    props: {
                      href:
                        ledgerId && accountId
                          ? makePath(
                              [
                                'w',
                                workspaceId,
                                'projects',
                                ...(schemaKey ? ['schema', schemaKey] : []),
                                ledgerId,
                                'accounts',
                                accountId,
                                'balances',
                                'total',
                              ],
                              { absolute: true }
                            )
                          : '',
                      children: 'Total',
                    },
                  },
                  own: {
                    props: {
                      href:
                        ledgerId && accountId
                          ? makePath(
                              [
                                'w',
                                workspaceId,
                                'projects',
                                ...(schemaKey ? ['schema', schemaKey] : []),
                                ledgerId,
                                'accounts',
                                accountId,
                                'balances',
                                'own',
                              ],
                              { absolute: true }
                            )
                          : '',
                      children: 'Own',
                    },
                  },
                  child: {
                    props: {
                      href:
                        ledgerId && accountId
                          ? makePath(
                              [
                                'w',
                                workspaceId,
                                'projects',
                                ...(schemaKey ? ['schema', schemaKey] : []),
                                ledgerId,
                                'accounts',
                                accountId,
                                'balances',
                                'child',
                              ],
                              { absolute: true }
                            )
                          : '',
                      children: 'Child',
                    },
                  },
                },
              },
              // Ideally, we would only display this page for linked accounts.
              // However, we can't infer linked-ness from the URL path, so we can't
              // do that without consideration for loading states.
              unreconciled: {
                props: {
                  href:
                    ledgerId && accountId
                      ? makePath(
                          [
                            'w',
                            workspaceId,
                            'projects',
                            ...(schemaKey ? ['schema', schemaKey] : []),
                            ledgerId,
                            'accounts',
                            accountId,
                            'unreconciled',
                          ],
                          { absolute: true }
                        )
                      : '',
                  children: 'Unreconciled',
                },
              },
            },
          },
          entries: {
            props: {
              href: ledgerId
                ? makePath(
                    [
                      'w',
                      workspaceId,
                      'projects',
                      ...(schemaKey ? ['schema', schemaKey] : []),
                      ledgerId,
                      'entries',
                    ],
                    { absolute: true }
                  )
                : '',
              children: 'Entries',
              'aria-disabled': !ledgerId,
            },
          },
          // Payment Types come from the Schema, so there are no Payments to
          // list on a Schema-less Ledger.
          ...(schemaKey &&
            isPaymentsEnabled && {
              payments: {
                props: {
                  href: ledgerId
                    ? makePath(
                        [
                          'w',
                          workspaceId,
                          'projects',
                          'schema',
                          schemaKey,
                          ledgerId,
                          'payments',
                        ],
                        { absolute: true }
                      )
                    : '',
                  children: 'Payments',
                  'aria-disabled': !ledgerId,
                },
              },
            }),
          ...(hasGroups && {
            groups: {
              props: {
                href: ledgerId
                  ? makePath(
                      [
                        'w',
                        workspaceId,
                        'projects',
                        ...(schemaKey ? ['schema', schemaKey] : []),
                        ledgerId,
                        'groups',
                      ],
                      { absolute: true }
                    )
                  : '',
                children: 'Groups',
                'aria-disabled': !ledgerId,
              },
              visibleOverride: active.startsWith('projects.ledgers.groups.'),
              subNav: {
                entries: {
                  props: {
                    href:
                      ledgerId && groupId
                        ? makePath(
                            [
                              'w',
                              workspaceId,
                              'projects',
                              ...(schemaKey ? ['schema', schemaKey] : []),
                              ledgerId,
                              'groups',
                              groupId,
                              'entries',
                            ],
                            { absolute: true }
                          )
                        : '',
                    children: 'Entries',
                    'aria-disabled': !ledgerId,
                  },
                },
                balances: {
                  props: {
                    href:
                      ledgerId && groupId
                        ? makePath(
                            [
                              'w',
                              workspaceId,
                              'projects',
                              ...(schemaKey ? ['schema', schemaKey] : []),
                              ledgerId,
                              'groups',
                              groupId,
                              'balances',
                            ],
                            { absolute: true }
                          )
                        : '',
                    children: 'Balances',
                  },
                },
              },
            },
          }),
          ...(schemaKey &&
            hasMigrations && {
              migrations: {
                props: {
                  href: ledgerId
                    ? makePath(
                        [
                          'w',
                          workspaceId,
                          'projects',
                          'schema',
                          schemaKey,
                          ledgerId,
                          'migrations',
                        ],
                        { absolute: true }
                      )
                    : '',
                  children: 'Migrations',
                  'aria-disabled': !ledgerId,
                },
              },
            }),
        },
      },
    };
    setActiveNavTreePath(navTree, active);
    return navTree;
  }, [
    active,
    workspaceId,
    schemaKey,
    ledgerId,
    accountId,
    groupId,
    isPaymentsEnabled,
    hasGroups,
    hasMigrations,
  ]);
};
